import { PreferenceGroupSubTypeEnum } from 'api-hooks/preference-tag';
import { FormState, useFormState } from 'components/elements/form';
import { flightCheckInOptions } from 'components/elements/table/editable-columns/check-in-options';
import preferenceColumn from 'components/elements/table/editable-columns/preference-column';
import IndeterminateCheckbox from 'components/elements/table/table-checkbox';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType } from 'components/elements/table/type';
import useGetAirlineColumn from 'hooks/use-get-airline-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import styles from '../../styles.module.css';
import { FlightRescheduleFormType } from '../form-type';

interface Props {
  parentIndex: number;
}

function reducer(state, action, { update, clearErrors, name, setValue }) {
  switch (action.type) {
    case 'update_cell':
      return {
        ...state,
        data: state.data.map((row, index) => {
          if (index === action.rowIndex) {
            const tempValue = {
              ...state.data[action.rowIndex],
              [action.columnId]: action.value,
            };

            clearErrors(name);
            setValue(`${name}.${index}`, tempValue, { shouldValidate: true });
            return tempValue;
          }
          return row;
        }),
      };
    case 'update_all':
      return {
        ...state,
        data: action.newData,
      };
    case 'isSelected':
      return {
        ...state,
        data: state.data.map((row, index) => {
          const tempValue = {
            ...state.data[index],
            isSelected: action.value,
          };
          clearErrors(name);
          setValue(`${name}.${index}`, tempValue, { shouldValidate: true });
          // update(index, tempValue);
          return tempValue;
        }),
      };
    default:
  }
}

function HeaderCheckbox(props: { index: number; dispatch }) {
  const flightGuest = useWatch({
    name: `flightSales[${props.index}].flightSaleGuests`,
  });

  const indeterminate = flightGuest.some((curr) => curr.isSelected);
  const allChecked = flightGuest.every((curr) => curr.isSelected);

  return (
    <FormState>
      {({ editable }) => (
        <div className={styles.checkboxCenter}>
          <IndeterminateCheckbox
            indeterminate={indeterminate}
            checked={allChecked}
            disabled={!editable}
            onChange={(event) => {
              props.dispatch?.({
                type: 'isSelected',
                value: event.currentTarget.checked,
              });
            }}
          />
        </div>
      )}
    </FormState>
  );
}

function useGetRescheduleFlightColumns(parentIndex: number) {
  const { t } = useTranslation();
  const airlineColumn = useGetAirlineColumn();
  return (dispatch) => [
    {
      header: () => <HeaderCheckbox index={parentIndex} dispatch={dispatch} />,
      accessorKey: 'isSelected',
      minSize: 50,
      size: 50,
      checkboxNoLabel: true,
      dataType: ColumnDataType.checkbox,
    },
    {
      header: t('common:guest'),
      accessorKey: 'guestName',
      minSize: 150,
      dataType: ColumnDataType.text,
      disabled: true,
    },
    {
      header: t('common:status'),
      accessorKey: 'checkInStatus',
      minSize: 150,
      dataType: ColumnDataType.select,
      options: flightCheckInOptions(t),
    },
    {
      header: t('common:ticket'),
      accessorKey: 'ticket',
      minSize: 100,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:airline'),
      accessorKey: 'airlineId',
      minSize: 100,
      dataType: ColumnDataType.select,
      selectProps: airlineColumn,
      required: true,
    },
    {
      header: t('common:date'),
      accessorKey: 'date',
      minSize: 100,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:detail'),
      accessorKey: 'detail',
      minSize: 100,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:class'),
      accessorKey: 'preferenceId',
      minSize: 200,
      dataType: ColumnDataType.select,
      selectProps: preferenceColumn({
        subType: PreferenceGroupSubTypeEnum.FlightClass,
      }),
    },
    {
      header: t('common:route'),
      accessorKey: 'route',
      minSize: 100,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:time'),
      accessorKey: 'time',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:price'),
      accessorKey: 'tempPrice',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
    },
    {
      header: t('common:reschedule_price'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
  ];
}

export default function FlightRescheduleGuestTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const {
    control,
    formState: { isSubmitting },
    clearErrors,
    setValue,
  } = useFormContext<FlightRescheduleFormType>();

  const name: any = `flightSales.${props.parentIndex}.flightSaleGuests`;

  const [isInitiate, setIsInitiate] = React.useState<{
    value: boolean;
    data: any;
  }>({ value: false, data: undefined });

  const { fields, update } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });
  const _columns = useGetRescheduleFlightColumns(props.parentIndex);
  const columns = (dispatch?: any) => _columns(dispatch);

  const [state, dispatch] = React.useReducer(
    (state, action) =>
      reducer(state, action, { update, clearErrors, name, setValue }),
    {
      columns: columns(),
      data: [],
    },
  );

  React.useEffect(() => {
    if (!isInitiate.value) {
      dispatch({
        type: 'update_all',
        newData: fields.length ? fields : [],
      });
      setIsInitiate({
        value: true,
        data: fields.length ? fields : [],
      });
    }
  }, [fields, isInitiate]);

  React.useEffect(() => {
    if (!editable && isInitiate.value && !isSubmitting) {
      dispatch({ type: 'update_all', newData: isInitiate.data });
    }
  }, [editable, isInitiate.value, isSubmitting, t, isInitiate.data]);

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns(dispatch) as any}
        data={state.data}
        dispatch={dispatch}
        name={name}
        disabled={!editable}
        uniqueRowKey="customId"
        onCustomDisabledCell={({ accessorKey, rowOriginal }) =>
          !!(accessorKey !== 'isSelected' && !rowOriginal.isSelected)
        }
        noAddRemoveRow
      />
    </div>
  );
}
